import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import * as XLSX from 'xlsx';

@Component({ standalone: false,
  selector: 'app-izipay-excel-generator',
  templateUrl: './izipay-excel-generator.component.html',
  styleUrls: ['./izipay-excel-generator.component.css']
})
export class IzipayExcelGeneratorComponent implements OnInit {
  apiUrl: string = 'https://izipay-file-service-858281476358.us-east1.run.app';
  fileName: string = 'PMP_VM_20536557858_2_20240129.txt';
  selectedFile: File | null = null;
  isLoading: boolean = false;
  statusMessage: string = '';
  statusClass: string = '';

  constructor(private http: HttpClient) {}

  ngOnInit() {}

  showStatus(type: string, message: string) {
    this.statusClass = `status ${type}`;
    this.statusMessage = message;
    setTimeout(() => {
      this.statusMessage = '';
    }, 5000);
  }

  downloadTemplate() {
    const templateData = [
      ['Token'],
      ['PLV-112-36c0ebac-87cc-46e8-8df2-782af516a911Kaj'],
      ['PLV-96-7c4e6f91-231f-43ba-b854-33141fda297dAyQd']
    ];
    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tokens');
    XLSX.writeFile(wb, 'izipay-batch-tokens-template.xlsx');
    this.showStatus('success', 'Plantilla descargada. Completá la columna Token (un token por fila).');
  }

  handleFileSelect(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.selectedFile = file;
      this.showStatus('info', `Archivo seleccionado: ${file.name}`);
    }
  }

  async generateFromExcel() {
    if (!this.selectedFile) {
      this.showStatus('error', 'Por favor selecciona un archivo Excel');
      return;
    }

    try {
      this.isLoading = true;
      this.showStatus('info', 'Leyendo Excel y extrayendo tokens...');

      const workbook = await this.readExcelFile(this.selectedFile);
      const tokens = this.extractTokensFromExcel(workbook);

      if (tokens.length === 0) {
        this.showStatus('error', 'No se encontraron tokens. El Excel debe tener una columna "Token" con al menos un valor.');
        return;
      }

      await this.callAPIWithTokens(tokens);
    } catch (error: any) {
      console.error('Error:', error);
      this.showStatus('error', error?.message || 'Error al procesar el archivo');
    } finally {
      this.isLoading = false;
    }
  }

  /** Extrae la lista de tokens desde la primera hoja del Excel (columna Token, token, TOKEN o primera columna). */
  extractTokensFromExcel(workbook: any): string[] {
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

    if (!jsonData || jsonData.length < 2) {
      return [];
    }

    const headers = (jsonData[0] || []).map((h: any) => String(h || '').trim());
    const tokenColIndex = headers.findIndex(
      (h: string) => /^token$/i.test(h) || /^payment\s*token$/i.test(h) || h === 'Token'
    );
    const colIndex = tokenColIndex >= 0 ? tokenColIndex : 0;
    const tokens: string[] = [];

    for (let i = 1; i < jsonData.length; i++) {
      const row = jsonData[i] || [];
      const cell = row[colIndex];
      const token = cell != null ? String(cell).trim() : '';
      if (token.length > 0) {
        tokens.push(token);
      }
    }

    return [...new Set(tokens)];
  }

  readExcelFile(file: File): Promise<any> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          resolve(workbook);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  /** Llama al endpoint /izipay-batch/generate con tokens; el backend usa el projectId de su entorno. */
  async callAPIWithTokens(tokens: string[]) {
    const url = `${this.apiUrl.replace(/\/$/, '')}/izipay-batch/generate`;
    const body = {
      fileName: this.fileName || 'izipay-f5.txt',
      tokens
    };

    try {
      this.showStatus('info', 'Enviando tokens al servicio y generando TXT...');

      const response = await firstValueFrom(
        this.http.post(url, body, { responseType: 'blob' })
      );

      const blob = new Blob([response as Blob], { type: 'text/plain' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = body.fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);

      this.showStatus('success', `Archivo ${body.fileName} generado con ${tokens.length} token(s).`);
    } catch (error: any) {
      const msg = error?.error?.message || error?.message || 'Error al llamar al servicio';
      this.showStatus('error', msg);
      throw error;
    }
  }
}